import { type MouseEvent, useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_FEEDBACK_MS = 1500;

export type UseCopyToClipboardOptions = {
  feedbackMs?: number;
  /** Called when `navigator.clipboard.writeText` rejects (e.g. permission denied). */
  onError?: () => void;
};

export function useCopyToClipboard(options: UseCopyToClipboardOptions = {}) {
  const { feedbackMs = DEFAULT_FEEDBACK_MS, onError } = options;
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const copyTimeoutRef = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (copyTimeoutRef.current != null) {
        window.clearTimeout(copyTimeoutRef.current);
      }
    },
    []
  );

  const copy = useCallback(
    async (text: string, key: string, event?: MouseEvent) => {
      event?.stopPropagation();
      try {
        await navigator.clipboard.writeText(text);
        setCopiedKey(key);
        if (copyTimeoutRef.current != null) {
          window.clearTimeout(copyTimeoutRef.current);
        }
        copyTimeoutRef.current = window.setTimeout(() => {
          setCopiedKey(null);
          copyTimeoutRef.current = null;
        }, feedbackMs);
      } catch {
        onError?.();
      }
    },
    [feedbackMs, onError]
  );

  const isCopied = useCallback((key: string) => copiedKey === key, [copiedKey]);

  return { copiedKey, copy, isCopied };
}
